import React from 'react';

export default function DeployTroopsConfirmation({ 
  sourceCountry, 
  targetCountry, 
  onConfirm, 
  onCancel,
  lastDeployedTurn,
  currentTurn,
  gameId
}) {
  const [successRate, setSuccessRate] = React.useState(70); // Default 70%
  const [loading, setLoading] = React.useState(true);

  // Fetch real success rate
  React.useEffect(() => {
    const fetchSuccessRate = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(
          `${API_URL}/api/games/${gameId}/success-rates/${sourceCountry.code}/${targetCountry.code}`
        );
        const rates = await response.json();
        setSuccessRate(rates.deploy_troops || 70);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch success rate:', error);
        setSuccessRate(70); // Fallback
        setLoading(false);
      }
    };
    fetchSuccessRate();
  }, [gameId, sourceCountry.code, targetCountry.code]);

  // Calculate cooldown
  const turnsSinceLastDeploy = currentTurn - (lastDeployedTurn || 0);
  const turnsUntilAvailable = Math.max(0, 5 - turnsSinceLastDeploy);
  const onCooldown = turnsUntilAvailable > 0;

  // Check if player has resources
  const hasResources = 
    sourceCountry.resources.money >= 60 &&
    sourceCountry.resources.military >= 30 &&
    sourceCountry.resources.influence >= 15;
  
  const failureRate = 100 - successRate;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm p-4"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onCancel(); // Click backdrop to cancel
      }}
      style={{ pointerEvents: 'auto' }}
    >
      <div 
        className="bg-gradient-to-br from-red-900 via-gray-900 to-black border-4 border-red-600 rounded-lg max-w-2xl w-full shadow-2xl scrollbar-custom"
        style={{ 
          maxHeight: '90vh',
          overflowY: 'auto',
          pointerEvents: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#dc2626 #1f2937'
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <style jsx>{`
          .scrollbar-custom::-webkit-scrollbar {
            width: 12px;
          }
          .scrollbar-custom::-webkit-scrollbar-track {
            background: #1f2937;
            border-radius: 10px;
          }
          .scrollbar-custom::-webkit-scrollbar-thumb {
            background: #dc2626;
            border-radius: 10px;
            border: 2px solid #1f2937;
          }
          .scrollbar-custom::-webkit-scrollbar-thumb:hover {
            background: #ef4444;
          }
        `}</style>
        <div className="p-8">{/* Wrapper for padding */}
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-2">⚔️💥🌍</div>
          <h2 className="text-4xl font-bold text-red-500 mb-2">
            DECLARATION OF WAR
          </h2>
          <p className="text-xl text-gray-300">
            {sourceCountry.name} vs {targetCountry.name}
          </p>
        </div>

        {/* Cooldown Warning */}
        {onCooldown && (
          <div className="bg-yellow-900 border-2 border-yellow-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">⏰</span>
              <div>
                <p className="text-yellow-300 font-bold text-lg">WAR EXHAUSTION</p>
                <p className="text-yellow-200">
                  Your military needs {turnsUntilAvailable} more turn{turnsUntilAvailable > 1 ? 's' : ''} to recover before next deployment.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Resource Check */}
        {!hasResources && !onCooldown && (
          <div className="bg-red-900 border-2 border-red-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl">❌</span>
              <div>
                <p className="text-red-300 font-bold text-lg">INSUFFICIENT RESOURCES</p>
                <p className="text-red-200">Required: $60M, 30 Military, 15 Influence</p>
              </div>
            </div>
          </div>
        )}

        {/* Cost */}
        <div className="bg-gray-800 border-2 border-gray-600 rounded-lg p-4 mb-6">
          <p className="text-gray-300 font-bold mb-2">⚡ IMMEDIATE COST:</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className={sourceCountry.resources.money >= 60 ? 'text-green-400' : 'text-red-400'}>
              <div className="text-2xl">💰</div>
              <div className="font-bold">$60M</div>
            </div>
            <div className={sourceCountry.resources.military >= 30 ? 'text-green-400' : 'text-red-400'}>
              <div className="text-2xl">🪖</div>
              <div className="font-bold">30 Military</div>
            </div>
            <div className={sourceCountry.resources.influence >= 15 ? 'text-green-400' : 'text-red-400'}>
              <div className="text-2xl">⭐</div>
              <div className="font-bold">15 Influence</div>
            </div>
          </div>
        </div>

        {/* Success Rewards */}
        <div className="bg-green-900 bg-opacity-50 border-2 border-green-500 rounded-lg p-4 mb-4">
          <p className="text-green-400 font-bold mb-3 flex items-center gap-2">
            <span className="text-2xl">✅</span> IF SUCCESSFUL ({loading ? '...' : `${successRate}%`} chance):
          </p>
          <ul className="space-y-2 text-green-300 text-sm">
            <li>🏆 Seize 30% of {targetCountry.name}'s ALL resources</li>
            <li>💎 Gain permanent territory bonus (+$15M per turn)</li>
            <li>⚔️ Military dominance (+30% to future actions vs {targetCountry.name})</li>
            <li>☢️ Capture uranium facilities (+10% enrichment)</li>
          </ul>
        </div>

        {/* Failure Consequences */}
        <div className="bg-red-900 bg-opacity-50 border-2 border-red-500 rounded-lg p-4 mb-4">
          <p className="text-red-400 font-bold mb-3 flex items-center gap-2">
            <span className="text-2xl">❌</span> IF FAILED ({loading ? '...' : `${failureRate}%`} chance):
          </p>
          <ul className="space-y-2 text-red-300 text-sm">
            <li>💔 Lose HALF your military (devastating defeat)</li>
            <li>💸 Economic collapse (-$40M additional loss)</li>
            <li>🌍 Global pariah (all relations -20)</li>
            <li>☢️ {targetCountry.name} panic-builds nukes (+30% enrichment)</li>
          </ul>
        </div>

        {/* Global Consequences (Always) */}
        <div className="bg-yellow-900 bg-opacity-50 border-2 border-yellow-500 rounded-lg p-4 mb-6">
          <p className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
            <span className="text-2xl">🌐</span> GLOBAL CONSEQUENCES (Always Happen):
          </p>
          <ul className="space-y-2 text-yellow-300 text-sm">
            <li>💀 Massive casualties: You lose 5-10M population, {targetCountry.name} loses 15-30M</li>
            <li>🚫 Economic sanctions from neutral countries (-$30M per turn for 3 turns)</li>
            <li>😡 All allies of {targetCountry.name} lose -20 relations with you</li>
            <li>⏰ War exhaustion: 2 more turns before deployment</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-4 px-6 rounded-lg text-lg transition-all"
          >
            🤔 RECONSIDER
          </button>
          <button
            onClick={onConfirm}
            disabled={onCooldown || !hasResources}
            className={`flex-1 font-bold py-4 px-6 rounded-lg text-lg transition-all ${
              onCooldown || !hasResources
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-500 text-white animate-pulse'
            }`}
          >
            {onCooldown ? '⏰ ON COOLDOWN' : !hasResources ? '❌ INSUFFICIENT' : '⚔️ DEPLOY TROOPS'}
          </button>
        </div>

        {/* Fine Print */}
        <p className="text-center text-gray-500 text-xs mt-4">
          This action cannot be undone. Choose wisely, Commander.
        </p>
        
        </div>{/* End padding wrapper */}
      </div>
    </div>
  );
}
